import * as React from 'react';
import { memo } from 'react';
import { Row, useTable } from 'react-table';

import styled from 'styled-components';

import { InfiniteScrollContainer, LoadingData } from 'molecules';
import {
  CheckBox,
  Icon,
  IconsNames,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from 'atoms';
import { Gradients, IAttendances, IHomeworkMarks } from 'components/types';
import { ChangeAttendanceMutationVariables } from 'core/graphql';
import { Colors } from 'services/ColorService';
import { DateService } from 'services/DateService';

export type IRow = {
  name: string;
  /** ТЗ 0026: нужен, чтобы найти оценку за ДЗ в матрице. */
  studentId?: string;
  agreementIsActive?: boolean | null;
  attendances: IAttendances;
};

export enum Accesors {
  name = 'name',
  attendances = 'attendances',
}

export interface IColumn {
  accessor: Accesors;
  /** ТЗ 0026: id занятия для колонки-занятия (у первой колонки его нет). */
  lessonId?: string;
  flexible?: boolean;
  align?: 'left' | 'right' | 'center';
  Header?: string | React.ReactElement;
  paddingRight?: string;
  paddingLeft?: string;
  columnWidth?: string;
}

export interface IJournalTableComponent {
  columns: IColumn[];
  rows: IRow[];
  isFlexible: boolean;
  noDataText: string;
  hasMore: boolean;
  afterStudent: string;
  loading: boolean;
  loadMore: (after: string) => void;
  updateAttendance: (variables: ChangeAttendanceMutationVariables) => void;
  /** ТЗ 0026: оценки за ДЗ, lessonId → studentId → ячейка. */
  homeworkMarks?: IHomeworkMarks;
  /** ТЗ 0026: методист видит журнал, но не правит его. */
  canEditAttendance?: boolean;
  /** Подписи для тултипов — приходят из контейнера, чтобы не тащить сюда i18n. */
  labels?: {
    auto: string;
    manual: string;
    hwDone: string;
    hwErrors: string;
    hwNotDone: string;
    hwSubmitted: string;
  };
}

/** Человеческая подпись «кто и когда поставил отметку» для тултипа ячейки. */
const buildAttendanceTitle = (labels?: IJournalTableComponent['labels']) => (
  source: string,
  markedAt: string | null,
  markedByName: string | null,
): string => {
  if (!source || !labels) {
    return '';
  }
  // Своя короткая дата, а не DateService: у него нет форматтера «дата +
  // время», а заводить его ради тултипа не стоит.
  const when = markedAt ? new Date(markedAt).toLocaleString() : '';
  if (source === 'auto') {
    return [labels.auto, when].filter(Boolean).join(' · ');
  }
  return [labels.manual, markedByName, when].filter(Boolean).join(' · ');
};

export const JournalTableComponent = memo(
  ({
    isFlexible,
    noDataText,
    rows: rowsSource,
    columns,
    hasMore,
    afterStudent,
    loading,
    homeworkMarks,
    canEditAttendance = true,
    labels,
    loadMore,
    updateAttendance,
  }: IJournalTableComponent) => {
    const attendanceTitle = buildAttendanceTitle(labels);
    const hwTitle = (mark: string | null, submitted: boolean): string => {
      if (!labels) {
        return '';
      }
      if (mark === 'done') return labels.hwDone;
      if (mark === 'contains_errors') return labels.hwErrors;
      if (mark === 'not_done') return labels.hwNotDone;
      return submitted ? labels.hwSubmitted : '';
    };

    const {
      getTableProps,
      headers,
      getTableBodyProps,
      rows,
      prepareRow,
    } = useTable({
      columns,
      data: rowsSource,
    });

    return (
      <Wrapper>
        <InfiniteScrollContainer
          loadMore={() => loadMore(afterStudent)}
          hasMore={hasMore}
          dataLength={rows.length}
          loading={loading || false}
        >
          <Table
            {...getTableProps()}
            flexible={isFlexible}
            variant="defaultJournal"
          >
            <TableHead>
              <TableRow type="row">
                {headers.map((column: any) => {
                  const {
                    key: cellKey,
                    ...restCellProps
                  } = column.getHeaderProps({
                    type: 'headerCell',
                    flexible: column.flexible && isFlexible,
                    width: column.columnWidth,
                    paddings: {
                      right: column.paddingRight,
                      left: column.paddingLeft,
                    },
                  });

                  return (
                    <TableCell key={cellKey} {...restCellProps}>
                      {column.render('Header')}
                    </TableCell>
                  );
                })}
              </TableRow>
            </TableHead>

            <TableBody {...getTableBodyProps()}>
              {!rows.length && !loading && (
                <WrappedTableRow key="noData">
                  <TableCell type="cell" width="100%" align="center">
                    {noDataText}
                  </TableCell>
                </WrappedTableRow>
              )}

              {loading && (
                <WrappedTableRow>
                  <TableCell width="100%" align="center">
                    <LoadingDataStyled />
                  </TableCell>
                </WrappedTableRow>
              )}

              {Boolean(rows.length) &&
                !loading &&
                rows.map((row: Row<IRow>) => {
                  prepareRow(row);
                  const { key: rowKey, ...restRowProps } = row.getRowProps();
                  const { agreementIsActive, studentId = '' } = row.original;

                  return (
                    <WrappedTableRow key={rowKey} {...restRowProps}>
                      {row.cells.map((cell: any, index) => {
                        const {
                          key: cellKey,
                          ...restCellProps
                        } = cell.getCellProps({
                          width: cell.column.columnWidth,
                          flexible: cell.column.flexible && isFlexible,
                          paddings: {
                            right: cell.column.paddingRight,
                            left: cell.column.paddingLeft,
                          },
                        });

                        const isAfterToday = DateService.isBefore(
                          new Date(),
                          cell.column.Header.props.value,
                        );

                        const isSameOrAfterToday = DateService.isSameOrBefore(
                          new Date(),
                          cell.column.Header.props.value,
                        );

                        if (index === 0) {
                          return (
                            <TableCell
                              key={cellKey}
                              textColor={
                                agreementIsActive
                                  ? Colors.Black
                                  : Colors.DeactivatedGrey
                              }
                              {...restCellProps}
                            >
                              {cell.render('Cell')}
                            </TableCell>
                          );
                        }

                        if (!cell?.value) {
                          return (
                            <EmptyJournalCell
                              key={cellKey}
                              borderColor={Colors.LightGray}
                              {...restCellProps}
                            >
                              <Icon
                                width={128}
                                height={56}
                                name={IconsNames.EmptyCell}
                              />
                            </EmptyJournalCell>
                          );
                        }

                        const {
                          id: attendanceId,
                          attendance,
                          source,
                          markedAt,
                          markedByName,
                        } = cell.value;
                        // ТЗ 0026: «не отмечен» — отдельное состояние. До сих
                        // пор null и false схлопывались в один красный, и
                        // незаполненный журнал выглядел как поголовный прогул.
                        const isUnmarked =
                          attendance === null || attendance === undefined;
                        const hw =
                          homeworkMarks?.[cell.column.lessonId]?.[studentId];

                        return (
                          <TableCheckBox
                            align="center"
                            checked={Boolean(attendance)}
                            key={cellKey}
                            isSameOrAfterToday={
                              isSameOrAfterToday || isUnmarked
                            }
                            borderColor={Colors.LightGray}
                            {...restCellProps}
                            agreementIsActive={agreementIsActive}
                          >
                            <CellStack>
                              <CellTop
                                title={attendanceTitle(
                                  source,
                                  markedAt,
                                  markedByName,
                                )}
                              >
                                <WrappedCheckBox
                                  variant={
                                    isSameOrAfterToday
                                      ? 'default'
                                      : 'tableCheckBox'
                                  }
                                  checked={
                                    isAfterToday ? false : Boolean(attendance)
                                  }
                                  onClick={() =>
                                    updateAttendance({
                                      attendanceId,
                                      attendance: !attendance,
                                    })
                                  }
                                  disabled={
                                    isAfterToday ||
                                    !agreementIsActive ||
                                    !canEditAttendance
                                  }
                                />
                                {!isUnmarked && source ? (
                                  <SourceMark $auto={source === 'auto'}>
                                    {source === 'auto' ? 'А' : 'Р'}
                                  </SourceMark>
                                ) : null}
                              </CellTop>
                              {hw ? (
                                <HwDot
                                  $mark={hw.hwMark}
                                  title={hwTitle(hw.hwMark, hw.submitted)}
                                />
                              ) : null}
                            </CellStack>
                          </TableCheckBox>
                        );
                      })}
                    </WrappedTableRow>
                  );
                })}
            </TableBody>
          </Table>
        </InfiniteScrollContainer>
      </Wrapper>
    );
  },
);

const Wrapper = styled.div`
  overflow-y: hidden;
`;

const WrappedTableRow = styled((props) => <TableRow {...props} />)`
  height: 56px;
`;

const TableCheckBox = styled((props) => <TableCell {...props} />)`
  height: 100%;
  padding: 20px 0;
  background: ${({ checked, isSameOrAfterToday, agreementIsActive }) => {
    if (isSameOrAfterToday) {
      if (!agreementIsActive) {
        return Colors.LightBlue;
      }
      return '';
    }

    if (!agreementIsActive) {
      return Colors.MainBackground;
    }

    if (checked) {
      return Gradients.Success;
    }

    return Gradients.Danger;
  }};
`;

const WrappedCheckBox = styled((props) => <CheckBox {...props} />)`
  width: 16px;
`;

const EmptyJournalCell = styled(TableCell)`
  height: 100%;
`;

const LoadingDataStyled = styled((props) => <LoadingData {...props} />)``;

/**
 * ТЗ 0026. Маркер источника отметки: «А» — засчитан вход по кнопке, «Р» —
 * поставил человек. Один символ в углу ячейки, потому что журнал и так
 * плотный, а вопрос «откуда это взялось» возникает точечно — на него отвечает
 * тултип.
 */
const SourceMark = styled.span<{ $auto?: boolean }>`
  position: absolute;
  top: 2px;
  right: 4px;
  font-size: 9px;
  line-height: 1;
  color: ${({ $auto }) => ($auto ? Colors.Black : Colors.DeactivatedGrey)};
`;

const CellStack = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
`;

const CellTop = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
`;

/** Оценка за ДЗ прямо под отметкой — раньше её в журнале не было вовсе. */
const HwDot = styled.span<{ $mark: string | null }>`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background-color: ${({ $mark }) => {
    if ($mark === 'done') return '#3CC13B';
    if ($mark === 'contains_errors') return '#FFB800';
    if ($mark === 'not_done') return '#F03738';
    return Colors.DeactivatedGrey;
  }};
`;
